import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageSeo from '@/seo/PageSeo';
import { BRAND_NAME } from '@/lib/brandConstants';

/**
 * SPA catch-all (React Router). Hostinger static 404.html is separate if you serve the built `index.html` for unknown paths.
 */
export default function NotFoundPage() {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-sky-50 via-white to-blue-50/80 text-gray-900">
      <PageSeo title="Page not found" description="The page you opened is not available." path="/404" />
      <div className="mx-auto flex max-w-lg flex-col items-center px-4 pb-16 pt-16 text-center sm:pt-24">
        <div className="mb-6 flex h-32 w-32 items-center justify-center rounded-3xl bg-white shadow-lg shadow-blue-900/10 ring-1 ring-blue-100">
          <svg viewBox="0 0 140 140" className="h-28 w-28 text-blue-600" aria-hidden>
            <circle cx="70" cy="70" r="52" fill="#eff6ff" stroke="currentColor" strokeWidth="2.5" opacity="0.35" />
            <text x="70" y="88" textAnchor="middle" fontSize="44" fontWeight="800" fill="currentColor">
              404
            </text>
            <circle cx="46" cy="48" r="5" fill="#1d4ed8" />
            <circle cx="94" cy="48" r="5" fill="#1d4ed8" />
            <path d="M48 92 Q70 72 92 92" fill="none" stroke="#1d4ed8" strokeWidth="4" strokeLinecap="round" />
          </svg>
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">This page wandered off</h1>
        <p className="mt-3 text-sm leading-relaxed text-gray-600 sm:text-base">
          The link may be broken, the page may have moved, or the address was mistyped. Let’s get you back to something
          familiar — no stress.
        </p>
        <div className="mt-10 flex w-full max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
          <Button asChild className="rounded-xl bg-blue-600 text-white hover:bg-blue-700">
            <Link to="/">
              <Home className="mr-2 h-4 w-4" aria-hidden />
              Back to home
            </Link>
          </Button>
          <Button asChild variant="outline" className="rounded-xl border-blue-200 bg-white/90 text-gray-800 hover:bg-blue-50/90">
            <Link to="/start">
              <Search className="mr-2 h-4 w-4" aria-hidden />
              Start assessment
            </Link>
          </Button>
        </div>
        <p className="mt-10 text-xs text-gray-400">{BRAND_NAME}</p>
      </div>
    </div>
  );
}
